/* ==========================================================================
   Scirocco — shell.js (T1)
   Comportamenti della struttura del sito. Vanilla JS, nessuna libreria.
   Tutto è protetto: se un elemento non c'è, non succede nulla.

   <sc-header>            header sticky: stato .is-scrolled (scroll passivo + rAF),
                          menu allineato all'header, chiusura del menu su desktop e sui link
   <sc-predictive-search> ricerca mentre scrivi: debounce 250 ms, AbortController, cache per query,
                          iniezione della sezione "predictive-search", frecce ↑/↓ tra i risultati
   <sc-assistant>         assistente "Dubbi?": pannello non modale, domande/risposte, Esc e click fuori
   + invio automatico del form paese/lingua e finestra newsletter (comparsa a tempo, memoria
     nel browser, copia del codice).

   Dialog, carrello, toast e animazioni arrivano da theme.js / motion.js (window.SC).
   ========================================================================== */
(() => {
  'use strict';

  const SC = (window.SC = window.SC || {});
  const CFG = window.SC_CONFIG || { routes: {}, settings: {}, strings: {} };
  const ROUTES = CFG.routes || {};
  const SETTINGS = CFG.settings || {};
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduced = () => (SC.reduced ? SC.reduced() : window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const define = (tag, cls) => {
    if (window.customElements && !customElements.get(tag)) customElements.define(tag, cls);
  };
  const debounce =
    SC.debounce ||
    ((fn, ms = 200) => {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), ms);
      };
    });

  /* ====================================================================
     Header
     ==================================================================== */
  class ScHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('[data-sc-header]');
      if (!this.header || this._bound) return;
      this._bound = true;
      this.menu = document.getElementById('sc-dialog-menu');
      this.scrolled = false;
      this.ticking = false;

      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.update();

      // il menu si apre allineato all'header (che a inizio pagina sta sotto al marquee)
      this.onDialogOpen = (e) => {
        if (e.detail && e.detail.id === 'menu') this.alignMenu();
      };
      document.addEventListener('sc:dialog:open', this.onDialogOpen);

      // passando a desktop il menu mobile non serve più
      this.mq = window.matchMedia('(min-width: 990px)');
      this.onBreakpoint = () => {
        if (this.mq.matches && SC.dialog && SC.dialog.isOpen('menu')) SC.dialog.close('menu', { restoreFocus: false });
      };
      if (this.mq.addEventListener) this.mq.addEventListener('change', this.onBreakpoint);

      // un link del menu porta altrove (o a un'ancora della stessa pagina): chiudiamo il pannello
      this.onMenuClick = (e) => {
        const link = e.target.closest && e.target.closest('a[href]');
        if (!link || link.hasAttribute('data-sc-open')) return;
        if (SC.dialog) SC.dialog.close('menu', { restoreFocus: false });
      };
      if (this.menu) this.menu.addEventListener('click', this.onMenuClick);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('sc:dialog:open', this.onDialogOpen);
      if (this.mq && this.mq.removeEventListener) this.mq.removeEventListener('change', this.onBreakpoint);
      if (this.menu && this.onMenuClick) this.menu.removeEventListener('click', this.onMenuClick);
      this._bound = false;
    }

    /** .is-scrolled quando l'header è agganciato in alto e la pagina scorre sotto. */
    update() {
      const top = this.header.getBoundingClientRect().top;
      const next = top <= 0.5 && window.scrollY > 8;
      if (next === this.scrolled) return;
      this.scrolled = next;
      this.header.classList.toggle('is-scrolled', next);
    }

    alignMenu() {
      if (!this.menu) return;
      const top = Math.max(0, Math.round(this.header.getBoundingClientRect().top));
      this.menu.style.setProperty('--sc-menu-offset', top + 'px');
    }
  }
  define('sc-header', ScHeader);

  /* ====================================================================
     Paese / lingua: invio automatico al cambio
     ==================================================================== */
  document.addEventListener('change', (e) => {
    const select = e.target && e.target.closest ? e.target.closest('select[data-sc-localization]') : null;
    if (!select) return;
    const form = select.closest('form');
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  });

  /* ====================================================================
     Ricerca mentre scrivi
     ==================================================================== */
  class ScPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="q"]');
      this.results = this.querySelector('[data-sc-predictive-results]');
      if (!this.form || !this.input || !this.results) return;
      this._bound = true;
      this.idle = this.querySelector('[data-sc-predictive-idle]');
      this.status = this.querySelector('[data-sc-predictive-status]');
      this.clearBtn = this.querySelector('[data-sc-search-clear]');
      this.cache = new Map();
      this.enabled = !!(SETTINGS.predictive && ROUTES.predictive_search);

      this.onInput = () => {
        this.syncClear();
        if (this.enabled) this.debounced();
      };
      this.debounced = debounce(() => this.search(), 250);
      this.onKeydown = (e) => this.handleKeys(e);
      this.onSubmit = (e) => {
        if (!this.input.value.trim()) e.preventDefault();
      };
      this.onDialogClose = (e) => {
        if (e.detail && e.detail.id === 'search') this.abort();
      };

      // listener legati a questa connessione: disconnectedCallback li toglie tutti con abort()
      this.listeners = new AbortController();
      const signal = this.listeners.signal;
      this.input.addEventListener('input', this.onInput, { signal });
      this.input.addEventListener('keydown', this.onKeydown, { signal });
      this.results.addEventListener('keydown', this.onKeydown, { signal });
      this.form.addEventListener('submit', this.onSubmit, { signal });
      document.addEventListener('sc:dialog:close', this.onDialogClose, { signal });
      if (this.clearBtn) {
        this.clearBtn.addEventListener(
          'click',
          () => {
            this.input.value = '';
            this.syncClear();
            this.reset();
            this.input.focus();
          },
          { signal }
        );
      }
      this.syncClear();
    }

    disconnectedCallback() {
      this.abort();
      if (this.listeners) this.listeners.abort();
      this._bound = false;
    }

    syncClear() {
      if (this.clearBtn) this.clearBtn.hidden = !this.input.value;
    }

    abort() {
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
      this.loading(false);
    }

    loading(on) {
      this.classList.toggle('is-loading', !!on);
      if (on) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
    }

    reset() {
      this.results.innerHTML = '';
      if (this.idle) this.idle.hidden = false;
      if (this.status) this.status.textContent = '';
      this.loading(false);
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.abort();
        this.reset();
        return;
      }
      if (this.cache.has(q)) {
        this.abort(); // una risposta più lenta ancora in volo non deve sovrascrivere questa
        this.render(this.cache.get(q));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.loading(true);
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'product,page,collection',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const res = await fetch(`${ROUTES.predictive_search}?${params.toString()}`, { signal, headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error(`predictive ${res.status}`);
        const html = await res.text();
        const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sc-predictive]');
        if (!node) throw new Error('predictive: nessun contenuto');
        const markup = node.outerHTML;
        if (this.cache.size > 30) this.cache.clear();
        this.cache.set(q, markup);
        if (signal.aborted) return;
        this.render(markup);
      } catch (err) {
        if (err && err.name === 'AbortError') return; // ci pensa la richiesta successiva
        this.reset(); // niente errori a schermo: con Invio resta la ricerca completa
      }
    }

    render(markup) {
      this.loading(false);
      this.results.innerHTML = markup;
      if (this.idle) this.idle.hidden = true;
      const announce = this.results.querySelector('[data-sc-predictive-announce]');
      if (this.status) this.status.textContent = announce ? announce.textContent.trim() : '';
      if (announce) announce.remove();
      if (SC.emit) SC.emit('sc:dom:updated', { root: this.results });
    }

    /** ↓ / ↑ fra campo e risultati (Invio e Esc restano al browser e al dialog). */
    handleKeys(e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const items = $$('[data-sc-result]', this.results);
      if (!items.length) return;
      e.preventDefault();
      const current = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        const next = current < 0 ? 0 : Math.min(current + 1, items.length - 1);
        items[next].focus();
      } else if (current <= 0) {
        this.input.focus();
      } else {
        items[current - 1].focus();
      }
    }
  }
  define('sc-predictive-search', ScPredictiveSearch);

  /* ====================================================================
     Assistente "Dubbi?"
     ==================================================================== */
  class ScAssistant extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.panel = this.querySelector('.sc-assist__panel');
      this.fab = this.querySelector('[data-sc-assist-toggle]');
      if (!this.panel || !this.fab) return;
      this._bound = true;
      this.body = this.querySelector('[data-sc-assist-body]');
      this.questions = $$('[data-sc-assist-q]', this);

      // listener legati a questa connessione: disconnectedCallback li toglie tutti con abort()
      this.listeners = new AbortController();
      const signal = this.listeners.signal;
      this.fab.addEventListener('click', () => this.toggle(), { signal });
      const closeBtn = this.querySelector('[data-sc-assist-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close(true), { signal });
      this.questions.forEach((btn) => btn.addEventListener('click', () => this.answer(btn), { signal }));

      this.onDocPointer = (e) => {
        if (!this.contains(e.target)) this.close(false);
      };
      this.onKey = (e) => {
        if (e.key === 'Escape') this.close(true);
      };
      document.addEventListener('sc:dialog:open', () => this.close(false), { signal });

      this.setupTryon(signal);

      if (CFG.designMode) {
        this.section = this.closest('.shopify-section');
        const onSelect = (e) => {
          if (e.target === this.section) this.open();
        };
        const onDeselect = (e) => {
          if (e.target === this.section) this.close(false);
        };
        document.addEventListener('shopify:section:select', onSelect, { signal });
        document.addEventListener('shopify:block:select', onSelect, { signal });
        document.addEventListener('shopify:section:deselect', onDeselect, { signal });
      }
    }

    disconnectedCallback() {
      // questi due si aggiungono e tolgono a ogni apertura/chiusura del pannello
      document.removeEventListener('pointerdown', this.onDocPointer, true);
      document.removeEventListener('keydown', this.onKey);
      if (this.listeners) this.listeners.abort();
      this._bound = false;
    }

    /** Sulla scheda prodotto la CTA "Come mi stanno?" apre direttamente la prova virtuale. */
    setupTryon(signal) {
      const cta = this.querySelector('[data-sc-assist-tryon]');
      if (!cta || !document.getElementById('sc-dialog-tryon')) return;
      const label = cta.getAttribute('data-label-tryon');
      const text = cta.querySelector('[data-sc-assist-cta-label]');
      if (label && text) text.textContent = label;
      cta.addEventListener(
        'click',
        (e) => {
          if (!SC.dialog || !SC.dialog.get('tryon')) return;
          e.preventDefault();
          this.close(false);
          SC.dialog.open('tryon', { trigger: cta });
        },
        { signal }
      );
    }

    /** Aperto = classe .is-open: durante l'animazione di chiusura il pannello è ancora visibile ma già chiuso. */
    isOpen() {
      return this.panel.classList.contains('is-open');
    }

    toggle() {
      if (this.isOpen()) this.close(true);
      else this.open();
    }

    open() {
      if (this.isOpen()) return;
      clearTimeout(this.hideTimer); // riaperto durante l'animazione di chiusura: il pannello non va nascosto
      this.panel.hidden = false;
      void this.panel.offsetWidth; // forza il reflow: la transizione parte davvero
      this.panel.classList.add('is-open');
      this.fab.setAttribute('aria-expanded', 'true');
      document.addEventListener('pointerdown', this.onDocPointer, true);
      document.addEventListener('keydown', this.onKey);
      const first = this.questions[0];
      if (first) setTimeout(() => first.focus({ preventScroll: true }), 60);
    }

    close(restoreFocus) {
      if (!this.isOpen()) return;
      this.panel.classList.remove('is-open');
      this.fab.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', this.onDocPointer, true);
      document.removeEventListener('keydown', this.onKey);
      clearTimeout(this.hideTimer);
      const hide = () => {
        this.panel.hidden = true;
      };
      if (reduced()) hide();
      else this.hideTimer = setTimeout(hide, 240);
      if (restoreFocus) this.fab.focus();
    }

    answer(btn) {
      const wasOpen = btn.getAttribute('aria-expanded') === 'true';
      this.questions.forEach((q) => {
        const id = q.getAttribute('aria-controls');
        const panel = id ? document.getElementById(id) : null;
        q.setAttribute('aria-expanded', 'false');
        if (panel) panel.hidden = true;
      });
      if (wasOpen) return;
      const id = btn.getAttribute('aria-controls');
      const panel = id ? document.getElementById(id) : null;
      btn.setAttribute('aria-expanded', 'true');
      if (!panel) return;
      panel.hidden = false;
      if (this.body) {
        const top = Math.max(0, panel.offsetTop - 12);
        try {
          this.body.scrollTo({ top, behavior: reduced() ? 'auto' : 'smooth' });
        } catch (err) {
          this.body.scrollTop = top;
        }
      }
    }
  }
  define('sc-assistant', ScAssistant);

  /* ====================================================================
     Finestra newsletter: appare da sola dopo N secondi, una volta sola
     --------------------------------------------------------------------
     Memoria nel browser (localStorage): "done" per chi si è iscritto (non torna mai),
     una data per chi ha chiuso (non torna per i giorni impostati nella sezione).
     Non appare se c'è già un dialog aperto, se la pagina non è in primo piano,
     o se il visitatore sta ancora leggendo un form. Dopo l'invio Shopify ricarica
     la pagina: la finestra si riapre da sola sul codice.
     ==================================================================== */
  const POPUP_KEY = 'sc:newsletter';

  function popupState() {
    try {
      return JSON.parse(localStorage.getItem(POPUP_KEY) || '{}') || {};
    } catch (err) {
      return {};
    }
  }
  function popupRemember(patch) {
    try {
      localStorage.setItem(POPUP_KEY, JSON.stringify(Object.assign(popupState(), patch)));
    } catch (err) {
      /* navigazione privata: pazienza, riapparirà alla visita dopo */
    }
  }

  function initNewsletterPopup() {
    const dlg = document.querySelector('[data-sc-popup]');
    if (!dlg || !SC.dialog) return;

    const posted = !!dlg.querySelector('[data-sc-popup-posted]');
    const open = () => SC.dialog.open('newsletter');

    if (posted) {
      popupRemember({ done: true });
      requestAnimationFrame(open);
      return;
    }

    const state = popupState();
    if (state.done) return;
    const days = Number(dlg.dataset.days || 30);
    if (state.closed && Date.now() - state.closed < days * 864e5) return;

    const delay = Math.max(1, Number(dlg.dataset.delay || 8)) * 1000;
    const show = () => {
      if (document.querySelector('dialog.sc-dialog[open]')) return; // non copriamo carrello, menu, prova virtuale
      if (document.hidden) {
        document.addEventListener('visibilitychange', show, { once: true }); // scheda in secondo piano: aspetta
        return;
      }
      open();
    };
    const timer = setTimeout(show, delay);

    // chi manda il form non deve vedersi coprire la pagina
    document.addEventListener('submit', () => clearTimeout(timer), { once: true });

    document.addEventListener('sc:dialog:close', (e) => {
      if (!e.detail || e.detail.id !== 'newsletter') return;
      if (!popupState().done) popupRemember({ closed: Date.now() });
    });
  }

  /* copia del codice sconto (solo dentro la finestra newsletter) */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-sc-copy]');
    if (!btn) return;
    const hint = btn.querySelector('.sc-pop__code-hint');
    const done = () => {
      if (!hint || btn.dataset.state === 'done') return;
      const before = hint.textContent;
      btn.dataset.state = 'done';
      hint.textContent = btn.dataset.copied || before;
      setTimeout(() => {
        btn.dataset.state = '';
        hint.textContent = before;
      }, 1600);
    };
    const text = btn.getAttribute('data-sc-copy') || '';
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, () => {});
    else done();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initNewsletterPopup);
  else initNewsletterPopup();
})();
